import React, { useState } from 'react';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import DynamicQuestionSet from './DynamicQuestionSet';
import DynamicPhotoUpload from './DynamicPhotoUpload';
import { determineItemStatus, areItemPhotosRequired } from '../utils/configHelpers';
import { getMerchantHeaderClasses } from '../utils/merchantColors';
import { t } from '../utils/i18n';

/**
 * Item-Level Flow Component
 *
 * Handles pagination through multiple items
 * For each item:
 * 1. Shows item-specific questions
 * 2. Optionally shows item-specific photo upload
 */
const ItemLevelFlow = ({
  config,
  language = 'de',
  merchant = null,
  bookingItems,
  inventoryItems,
  onComplete,
  onBack = null, // Callback to navigate to previous section
  isLastSection = true // Whether this is the final section before submission
}) => {
  const [currentItemIndex, setCurrentItemIndex] = useState(0);
  const [itemAnswers, setItemAnswers] = useState({});
  const [itemPhotos, setItemPhotos] = useState({});
  const [showingPhotos, setShowingPhotos] = useState(false);

  const currentBookingItem = bookingItems[currentItemIndex];
  const currentInventoryItem = inventoryItems.find(
    inv => inv.id === currentBookingItem.inventory_item_id
  );

  const isFirstItem = currentItemIndex === 0;
  const isLastItem = currentItemIndex === bookingItems.length - 1;

  const itemData = {
    bookingItem: currentBookingItem,
    inventoryItem: currentInventoryItem
  };

  // Get item identifier for display
  const getItemIdentifier = () => {
    if (currentInventoryItem?.item_number) {
      return currentInventoryItem.item_number;
    }
    return currentBookingItem.item_name;
  };

  const handleQuestionsComplete = (answers) => {
    console.log('[ItemLevelFlow] Questions complete for item', currentItemIndex, 'answers:', answers);

    // Store answers for this item
    const itemKey = currentBookingItem.id;
    setItemAnswers(prev => ({
      ...prev,
      [itemKey]: answers
    }));

    // Check if photos are required
    const photoConfig = config.item_photos_config;
    const photosRequired = areItemPhotosRequired(answers, photoConfig);

    console.log('[ItemLevelFlow] Photos required?', photosRequired, 'photoConfig:', photoConfig);

    if (photoConfig?.enabled && (photosRequired || photoConfig.mode === 'optional')) {
      // Show photo upload
      console.log('[ItemLevelFlow] Showing photos for item', currentItemIndex);
      setShowingPhotos(true);
    } else {
      // No photos needed, move to next item
      console.log('[ItemLevelFlow] No photos needed, moving to next item');
      moveToNextItem(answers, []);
    }
  };

  const handlePhotosComplete = (photos) => {
    try {
      console.log('[ItemLevelFlow] Photos complete for item', currentItemIndex, 'photos:', photos.length);

      const itemKey = currentBookingItem.id;
      const answers = itemAnswers[itemKey];

      console.log('[ItemLevelFlow] Retrieved answers for item:', itemKey, 'answers:', answers);

      // Store photos for this item
      if (photos.length > 0) {
        setItemPhotos(prev => ({
          ...prev,
          [itemKey]: photos
        }));
        console.log('[ItemLevelFlow] Stored', photos.length, 'photos for item');
      }

      console.log('[ItemLevelFlow] Calling moveToNextItem');
      moveToNextItem(answers, photos);
    } catch (error) {
      console.error('[ItemLevelFlow] ❌ ERROR in handlePhotosComplete:', error);
      throw error;
    }
  };

  const moveToNextItem = (answers, photos) => {
    console.log('[ItemLevelFlow] moveToNextItem called, currentItemIndex:', currentItemIndex, 'isLastItem:', isLastItem);

    if (isLastItem) {
      // All items processed, complete
      // Pass current item's answers and photos directly since setItemAnswers /
      // setItemPhotos are async and the state may not yet include them
      console.log('[ItemLevelFlow] Last item completed, calling completeFlow');
      completeFlow(currentBookingItem.id, answers, photos);
    } else {
      // Move to next item
      console.log('[ItemLevelFlow] Moving to next item, setting currentItemIndex to', currentItemIndex + 1);
      setCurrentItemIndex(prev => prev + 1);
      setShowingPhotos(false);
    }
  };

  const completeFlow = (lastItemKey, lastItemAnswers, lastItemPhotos = []) => {
    // Merge state with the last item's answers AND photos (state updates from
    // setItemAnswers / setItemPhotos may not have taken effect yet since React
    // batches state updates within the same event handler)
    const allItemAnswers = {
      ...itemAnswers,
      ...(lastItemKey ? { [lastItemKey]: lastItemAnswers } : {})
    };

    const allItemPhotos = {
      ...itemPhotos,
      ...(lastItemKey && lastItemPhotos.length > 0
        ? { [lastItemKey]: lastItemPhotos }
        : {})
    };

    // Compile all item returns
    console.log('[ItemLevelFlow] completeFlow called');
    console.log('[ItemLevelFlow] allItemAnswers:', allItemAnswers);
    console.log('[ItemLevelFlow] allItemPhotos:', allItemPhotos);

    const itemReturns = bookingItems.map(bookingItem => {
      const itemKey = bookingItem.id;
      const answers = allItemAnswers[itemKey] || {};
      const inventoryItem = inventoryItems.find(
        inv => inv.id === bookingItem.inventory_item_id
      );

      const itemStatus = determineItemStatus(answers);
      console.log(`[ItemLevelFlow] Item ${bookingItem.id} - answers:`, answers, 'status:', itemStatus);

      return {
        booking_item_id: bookingItem.id,
        inventory_item_id: inventoryItem?.id || null,
        item_level_answers: answers,
        item_status: itemStatus,
        description: generateItemDescription(answers)
      };
    });

    console.log('[ItemLevelFlow] Final itemReturns:', itemReturns);
    console.log('[ItemLevelFlow] Calling onComplete with', itemReturns.length, 'items');

    onComplete?.({
      itemReturns,
      itemPhotos: allItemPhotos
    });
  };

  const generateItemDescription = (answers) => {
    const parts = [];
    for (const [key, value] of Object.entries(answers)) {
      if (key.endsWith('_details')) {
        parts.push(value);
      }
    }
    return parts.join('; ');
  };

  const handleBack = () => {
    if (showingPhotos) {
      // Go back to questions
      setShowingPhotos(false);
    } else if (!isFirstItem) {
      // Go back to previous item
      setCurrentItemIndex(prev => prev - 1);
      setShowingPhotos(false);
    } else if (onBack) {
      // First item, go back to previous section
      onBack();
    }
  };

  // Get item count text
  const itemCountText = t(language, 'itemProgress', currentItemIndex + 1, bookingItems.length);

  if (showingPhotos) {
    // Show photo upload for current item
    const photoConfig = config.item_photos_config;
    const itemAnswersForCurrent = itemAnswers[currentBookingItem.id] || {};
    const photosRequired = areItemPhotosRequired(itemAnswersForCurrent, photoConfig);

    // Add item identifier to header. The override is built for the booking's
    // language (with en as safety net) so the strict-language header pick in
    // DynamicPhotoUpload finds it for non-de/en customers too.
    const langBase = String(language || 'en').toLowerCase().split(/[-_]/)[0];
    const photoHeaderPrefix = `${config.show_item_count ? itemCountText + ' - ' : ''}${getItemIdentifier()}`;
    const photoConfigWithItem = {
      ...photoConfig,
      header_text: {
        en: `${photoHeaderPrefix} - ${t('en', 'photosHeader')}`,
        [langBase]: `${photoHeaderPrefix} - ${t(language, 'photosHeader')}`
      },
      min_photos: photosRequired && photoConfig.mode === 'required_if_issues' ? 1 : 0
    };

    return (
      <DynamicPhotoUpload
        key={`item-photos-${currentBookingItem.id}`}
        config={photoConfigWithItem}
        language={language}
        merchant={merchant}
        itemData={itemData}
        initialPhotos={itemPhotos[currentBookingItem.id] || []}
        onPhotosChange={(photos) => {
          const itemKey = currentBookingItem.id;
          setItemPhotos(prev => ({
            ...prev,
            [itemKey]: photos
          }));
        }}
        onComplete={handlePhotosComplete}
        onBack={handleBack}
      />
    );
  }

  // Show questions for current item
  // Add item identifier to header (language-aware, see photo header above)
  const questionLangBase = String(language || 'en').toLowerCase().split(/[-_]/)[0];
  const questionHeader = `${config.show_item_count ? itemCountText + ' - ' : ''}${getItemIdentifier()}`;
  const configWithItem = {
    ...config,
    header_text: {
      en: questionHeader,
      [questionLangBase]: questionHeader
    }
  };

  // Whether a photo step will follow these questions for the current item.
  // Mirrors the condition in handleQuestionsComplete so the question button
  // shows "Weiter" (not "Abschließen") when a photo upload comes next.
  const photoConfig = config.item_photos_config;
  const photosFollow = (answers) =>
    !!photoConfig?.enabled &&
    (areItemPhotosRequired(answers, photoConfig) || photoConfig.mode === 'optional');

  return (
    <DynamicQuestionSet
      key={`item-questions-${currentBookingItem.id}`}
      config={configWithItem}
      language={language}
      merchant={merchant}
      itemData={itemData}
      onComplete={handleQuestionsComplete}
      onBack={handleBack}
      isLastItem={isLastItem}
      isLastSection={isLastSection}
      photosFollow={photosFollow}
    />
  );
};

export default ItemLevelFlow;
